import { useApiConfig, useAuth } from 'auth';
import axios, { AxiosInstance, AxiosPromise, AxiosResponse } from 'axios';
import { Configuration } from 'oas';
import { QueryKey, UseQueryResult, useQuery } from '@tanstack/react-query';

interface ExtendedPromise<T> extends Promise<T> {
    cancel: () => void;
}

export function useApiQuery<T>(
    key: QueryKey,
    fetcher: (apiConfig: Configuration, axiosInstance: AxiosInstance) => Promise<AxiosResponse<T>>,
    options = {},
    apiVersion = 'v6',
    apiName?: string,
): UseQueryResult<T> {
    const { apiConfig, axiosInstance } = useApiConfig(apiVersion, apiName);
    const auth = useAuth();
    return useQuery({
        queryKey: key,

        queryFn: () => {
            const CancelToken = axios.CancelToken;
            const source = CancelToken.source();
            apiConfig.baseOptions = { cancelToken: source.token };
            const promise = fetcher(apiConfig, axiosInstance).then((response) => response?.data) as ExtendedPromise<T>;
            promise.cancel = () => {
                source.cancel('Query was cancelled by React Query');
            };
            return promise;
        },
        ...{
            enabled: auth.isAuthenticated,
            staleTime: 600000,
            ...options,
        },
    });
}

export function mockResponse<TData>(data: TData): AxiosPromise<TData> {
    return new Promise((resolve) =>
        resolve({
            data,
            status: 0,
            statusText: '',
            headers: [],
            config: {},
        }),
    );
}
